/**
 * Predimensionamiento de losas — espesor h.
 *
 *   Aligerada 1D:   h ≈ L / 25       (L = luz libre, cm)
 *   Maciza 1D:      h ≈ L / 30
 *   Aligerada 2D:   h ≈ perímetro / 180
 *   Maciza 2D:      h ≈ perímetro / 180  (con perímetro = 2·(La + Lb))
 *
 * Para peraltes típicos en Perú: 20, 25, 30 cm para aligerada; 12, 15, 17 cm
 * para maciza. Redondear al siguiente valor estándar.
 */

import { useState } from 'react';
import { PredimCard, Group, NumberRow, ResultRow, FormulaNote, roundTo } from './shared';
import { SlabSectionDiagram, SlabPlanDiagram, DiagramPanel, View2D3DToggle } from './diagrams';
import { Slab3D } from './views3d';
import { PredimToolbar } from './PredimToolbar';
import type { DesignFull } from '@/features/designs/types';

type Variant = 'aligerada_1d' | 'maciza_1d' | 'aligerada_2d' | 'maciza_2d';

interface PredimSlabProps {
  variant: Variant;
  initialInputs?: { L_slab?: number; Lb_slab?: number } | null;
  existing?: DesignFull | null;
}

const VARIANT_PRESETS: Record<Variant, { label: string; descr: string; oneWay: boolean; ribbed: boolean }> = {
  aligerada_1d: {
    label: 'Losa aligerada en una dirección',
    descr: 'Viguetas con ladrillos. h ≈ L / 25. Peraltes típicos: 17, 20, 25, 30 cm.',
    oneWay: true, ribbed: true,
  },
  maciza_1d: {
    label: 'Losa maciza en una dirección',
    descr: 'Placa sólida. h ≈ L / 30. Espesores típicos: 12, 15, 17, 20 cm.',
    oneWay: true, ribbed: false,
  },
  aligerada_2d: {
    label: 'Losa aligerada en dos direcciones',
    descr: 'Reticular. h ≈ perímetro / 180.',
    oneWay: false, ribbed: true,
  },
  maciza_2d: {
    label: 'Losa maciza en dos direcciones',
    descr: 'Placa cruzada. h ≈ perímetro / 180.',
    oneWay: false, ribbed: false,
  },
};

const TYPICAL_H_RIBBED = [17, 20, 25, 30];
const TYPICAL_H_SOLID = [12, 15, 17, 20, 25];

export function PredimSlab({ variant, initialInputs, existing }: PredimSlabProps) {
  const preset = VARIANT_PRESETS[variant];
  const [L, setL] = useState(initialInputs?.L_slab ?? 400);
  const [Lb, setLb] = useState(initialInputs?.Lb_slab ?? 500);

  // Compute h
  let h_required: number;
  let formula: string;
  if (preset.oneWay) {
    const k = preset.ribbed ? 25 : 30;
    h_required = L / k;
    formula = `h = L / ${k}`;
  } else {
    const perimeter = 2 * (L + Lb);
    h_required = perimeter / 180;
    formula = `h = perímetro / 180 = 2·(La + Lb) / 180`;
  }

  // Pick the next standard step UP
  const standard = preset.ribbed ? TYPICAL_H_RIBBED : TYPICAL_H_SOLID;
  const h_std = standard.find((s) => s >= h_required) ?? standard[standard.length - 1];
  const h_round = roundTo(h_required, 5);

  const exportInputs = { slab_variant: variant, L_slab: L, Lb_slab: Lb };
  const [view, setView] = useState<'2D' | '3D'>('2D');

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex justify-end">
        <PredimToolbar
          family="slabs"
          variant={variant}
          inputs={exportInputs}
          existing={existing}
        />
      </div>
      <PredimCard title={preset.label} subtitle={preset.descr}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Group title={preset.oneWay ? 'Luz libre' : 'Luces del paño'}>
            <NumberRow
              label={preset.oneWay ? 'L libre' : 'La (luz corta)'}
              value={L} onChange={setL} step={25} unit="cm"
            />
            {!preset.oneWay && (
              <NumberRow label="Lb (luz larga)" value={Lb} onChange={setLb} step={25} unit="cm" />
            )}
          </Group>
          <Group title="Criterio">
            <div className="text-[12px] font-mono" style={{ color: 'var(--text-secondary)' }}>
              {formula}
            </div>
          </Group>
        </div>

        <FormulaNote>
          Espesor preliminar por control de deflexiones (ACI 318 / E.060 9.5.2.1). Para condiciones
          que difieren (cargas grandes, paños alargados), verificar deflexiones reales en el módulo Losas.
        </FormulaNote>
      </PredimCard>

      <PredimCard title="Resultados">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-1">
            <ResultRow label="h teórico" value={`${h_required.toFixed(2)} cm`} />
            <ResultRow label="h redondeado" value={`${h_round} cm`} />
            <ResultRow label="h estándar sugerido" value={`${h_std} cm`} highlight />
            {!preset.oneWay && (
              <ResultRow label="Relación m = La/Lb" value={(L / Lb).toFixed(3)} />
            )}
          </div>
          <DiagramPanel title="Sección transversal">
            <SlabSectionDiagram h={h_std} isRibbed={preset.ribbed} isTwoWay={!preset.oneWay} />
          </DiagramPanel>
        </div>
        <div className="mt-3">
          <DiagramPanel
            title={view === '3D' ? 'Vista 3D del paño' : 'Vista en planta del paño'}
            toolbar={<View2D3DToggle value={view} onChange={setView} />}
          >
            {view === '3D' ? (
              <div style={{ width: 540, height: 320 }}>
                <Slab3D
                  h={h_std}
                  L={L}
                  Lb={preset.oneWay ? undefined : Lb}
                  isRibbed={preset.ribbed}
                  isTwoWay={!preset.oneWay}
                />
              </div>
            ) : (
              <SlabPlanDiagram L={L} Lb={preset.oneWay ? undefined : Lb} isTwoWay={!preset.oneWay} />
            )}
          </DiagramPanel>
        </div>
        {!preset.oneWay && L / Lb < 0.5 && (
          <div className="mt-2 rounded-[var(--radius-sm)] border p-2.5 text-[11px]"
            style={{
              background: 'rgba(245,158,11,0.08)',
              borderColor: 'rgba(245,158,11,0.40)',
              color: 'var(--text-primary)',
            }}>
            <strong style={{ color: 'var(--accent)' }}>Atención:</strong> con m &lt; 0.5 la losa
            trabaja prácticamente en UNA dirección. Usa la variante 1D.
          </div>
        )}

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Para diseño completo con cargas, flexión, cortante y deflexión real, usa el módulo <strong>Losas</strong>.
        </div>
      </PredimCard>
    </div>
  );
}
